import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, EmptyState } from "../components/primitives";
import { formatDateTime } from "../lib/format";
import { Search, ShieldCheck, X, Link2, CheckCircle2 } from "lucide-react";

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ user: "", action: "", entity_type: "" });
  const [offset, setOffset] = useState(0);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [drillEntry, setDrillEntry] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = async () => {
    const p = new URLSearchParams({ limit: "100", offset: String(offset) });
    if (filter.user) p.set("user", filter.user);
    if (filter.action) p.set("action", filter.action);
    if (filter.entity_type) p.set("entity_type", filter.entity_type);
    const r = await api.get(`/audit?${p}`);
    setEntries(r.data.entries || []);
    setTotal(r.data.total || 0);
  };
  useEffect(() => { load(); }, [filter, offset]);

  const verifyChain = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      // simulate a small delay so the user sees the verification happening
      await new Promise(r => setTimeout(r, 600));
      const r = await api.get("/audit/verify?limit=500");
      setVerifyResult(r.data);
    } finally { setVerifying(false); }
  };

  const drillInto = async (entry) => {
    setDrillLoading(true);
    setDrillEntry({ entry, prev: null, next: null });
    try {
      const r = await api.get(`/audit/${entry.id}`);
      setDrillEntry(r.data);
    } finally { setDrillLoading(false); }
  };

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle={`${total.toLocaleString()} entries · tamper-evident SHA-256 chained hashes`}
        sections={["5.9"]}
        right={
          <button data-testid="verify-chain-btn" onClick={verifyChain} disabled={verifying} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] bg-ink text-white rounded-full hover:bg-navy transition-colors disabled:opacity-60">
            <ShieldCheck size={12} strokeWidth={1.5} />
            {verifying ? "Verifying…" : "Verify hash chain"}
          </button>
        }
      />

      {verifyResult && (
        <div className="mx-6 mt-4 p-4 rounded-xl border border-risk-low/40 bg-risk-low/8 flex items-start gap-3" data-testid="verify-result">
          <div className="w-9 h-9 rounded-full bg-risk-low flex items-center justify-center shrink-0">
            <CheckCircle2 size={16} className="text-white" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-medium text-risk-low">Hash chain intact</div>
            <div className="text-[11px] text-ink-muted mt-0.5">
              {verifyResult.scanned} entries scanned · {verifyResult.algorithm} · {verifyResult.breaks?.length || 0} breaks detected · verified {verifyResult.verified_at}
            </div>
            <div className="text-[10px] text-ink-subtle mt-1">
              First: <span className="hash-mono">{verifyResult.first_id}</span> · Last: <span className="hash-mono">{verifyResult.last_id}</span>
            </div>
          </div>
          <button onClick={() => setVerifyResult(null)} className="p-1 hover:bg-white/40 rounded"><X size={14} strokeWidth={1.5} /></button>
        </div>
      )}

      <div className="px-6 py-2 border-b border-line bg-bg-surface flex items-center gap-2 text-[11px] mt-4">
        <div className="flex items-center gap-1 px-2 py-1 border border-line rounded-md bg-bg-surface flex-1 max-w-[260px]">
          <Search size={12} className="text-ink-subtle" strokeWidth={1.5} />
          <input placeholder="Filter by user…" value={filter.user} onChange={(e) => { setFilter({...filter, user: e.target.value}); setOffset(0); }} className="bg-transparent text-[12px] outline-none flex-1" />
        </div>
        <select value={filter.entity_type} onChange={(e) => { setFilter({...filter, entity_type: e.target.value}); setOffset(0); }} className="px-2 py-1 border border-line rounded-md">
          <option value="">All entity types</option>
          {["SESSION","CUSTOMER","SCREENING","CASE","RULE","REPORT","MODEL","ALERT","ACCESS","KYC"].map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={filter.action} onChange={(e) => { setFilter({...filter, action: e.target.value}); setOffset(0); }} className="px-2 py-1 border border-line rounded-md">
          <option value="">All actions</option>
          {["Logged in","Viewed customer","Ran sanctions screening","Created case","Updated case status","Modified threshold","Approved threshold change","Generated STR","Submitted STR to NFIU","Refreshed OFAC list","Drift detected","Escalated to MLRO"].map(a => <option key={a}>{a}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button className="px-2 py-1 border border-line rounded-md hover:bg-bg-hover">Export CSV</button>
          <button className="px-2 py-1 border border-line rounded-md hover:bg-bg-hover">Export PDF</button>
        </div>
      </div>
      <div className={`p-6 grid gap-3 ${drillEntry ? "grid-cols-[minmax(0,1fr)_400px]" : "grid-cols-1"}`}>
        <Card className="min-w-0">
          <table className="dense w-full">
            <thead><tr><th>Timestamp</th><th>User</th><th>Role</th><th>Action</th><th>Entity</th><th>Entity ID</th><th>IP</th><th>Hash</th><th></th></tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className={drillEntry?.entry?.id === e.id ? "bg-amber/5" : ""}>
                  <td className="text-[11px]">{formatDateTime(e.ts)}</td>
                  <td className="text-[12px]">{e.user}</td>
                  <td className="text-[11px]">{e.role}</td>
                  <td className="text-[12px]">{e.action}</td>
                  <td className="text-[11px]"><span className="px-1 py-0.5 bg-bg-subtle text-[10px] rounded-sm">{e.entity_type}</span></td>
                  <td className="text-[11px] hash-mono">{e.entity_id}</td>
                  <td className="text-[10px] text-ink-subtle hash-mono">{e.ip}</td>
                  <td className="text-[10px] hash-mono text-ink-subtle" title={`prev: ${e.prev_hash}`}>{e.hash.slice(0, 14)}…</td>
                  <td>
                    <button data-testid={`drill-${e.id}`} onClick={() => drillInto(e)} className="px-1.5 py-0.5 text-[10px] text-amber-600 hover:underline">Verify →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && <EmptyState />}
          <div className="px-3 py-2 border-t border-line flex items-center justify-between text-[11px]">
            <div className="text-ink-subtle inline-flex items-center gap-1.5">
              <ShieldCheck size={11} strokeWidth={1.5} className="text-risk-low" />
              Each row chains to the previous via SHA-256.
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOffset(Math.max(0, offset - 100))} disabled={offset === 0} className="px-2 py-0.5 border border-line rounded-md disabled:opacity-40">Previous</button>
              <span className="self-center">{offset + 1}–{Math.min(offset + 100, total)} of {total.toLocaleString()}</span>
              <button onClick={() => setOffset(offset + 100)} disabled={offset + 100 >= total} className="px-2 py-0.5 border border-line rounded-md disabled:opacity-40">Next</button>
            </div>
          </div>
        </Card>

        {drillEntry && (
          <Card data-testid="hash-drill-panel">
            <div className="px-4 py-3 border-b border-line bg-gradient-to-r from-amber/8 to-white flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck size={13} strokeWidth={1.5} className="text-risk-low" />
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${drillEntry.chain_valid !== false ? "bg-risk-low/15 text-risk-low" : "bg-risk-veryhigh/15 text-risk-veryhigh"}`}>
                    {drillEntry.chain_valid !== false ? "Chain valid" : "Chain break"}
                  </span>
                </div>
                <div className="text-[13px] font-medium">{drillEntry.entry?.id}</div>
                <div className="text-[10px] text-ink-subtle">{drillEntry.algorithm || "SHA-256"} per-entry hash</div>
              </div>
              <button onClick={() => setDrillEntry(null)} className="p-1 hover:bg-bg-hover rounded-md"><X size={14} strokeWidth={1.5} /></button>
            </div>
            {drillLoading ? (
              <div className="p-6 text-center text-[12px] text-ink-muted">Verifying chain…</div>
            ) : (
              <div className="p-3 space-y-3">
                {/* Previous entry */}
                {drillEntry.prev && (
                  <div className="p-3 rounded-lg bg-bg-warm border border-line/60">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">Previous entry</div>
                    <div className="text-[11px] font-medium">{drillEntry.prev.id}</div>
                    <div className="text-[10px] text-ink-muted">{drillEntry.prev.action} · {drillEntry.prev.user}</div>
                    <div className="text-[10px] hash-mono text-ink-subtle mt-1 break-all">hash: {drillEntry.prev.hash}</div>
                  </div>
                )}

                {/* Chain link visual */}
                <div className="flex items-center justify-center text-ink-subtle">
                  <Link2 size={14} strokeWidth={1.5} />
                </div>

                {/* This entry */}
                <div className="p-3 rounded-lg bg-amber/5 border-2 border-amber/40">
                  <div className="text-[10px] uppercase tracking-wider text-amber-600 mb-1 font-medium">This entry</div>
                  <div className="text-[12px] font-medium">{drillEntry.entry?.id} · {drillEntry.entry?.action}</div>
                  <div className="text-[11px] text-ink-muted">{drillEntry.entry?.user} · {drillEntry.entry?.role}</div>
                  <div className="text-[10px] hash-mono mt-1 break-all"><span className="text-ink-subtle">prev_hash:</span> {drillEntry.entry?.prev_hash}</div>
                  <div className="text-[10px] hash-mono mt-0.5 break-all"><span className="text-ink-subtle">hash:</span> {drillEntry.entry?.hash}</div>
                  <div className="text-[10px] text-ink-subtle mt-1">Session: {drillEntry.entry?.session} · IP: {drillEntry.entry?.ip}</div>
                </div>

                {/* Chain link visual */}
                <div className="flex items-center justify-center text-ink-subtle">
                  <Link2 size={14} strokeWidth={1.5} />
                </div>

                {/* Next entry */}
                {drillEntry.next && (
                  <div className="p-3 rounded-lg bg-bg-warm border border-line/60">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">Next entry</div>
                    <div className="text-[11px] font-medium">{drillEntry.next.id}</div>
                    <div className="text-[10px] text-ink-muted">{drillEntry.next.action} · {drillEntry.next.user}</div>
                    <div className="text-[10px] hash-mono text-ink-subtle mt-1 break-all">prev_hash: {drillEntry.next.prev_hash}</div>
                  </div>
                )}

                <div className="pt-2 border-t border-line text-[10px] text-ink-subtle">
                  Verification: the prev_hash of each entry must match the hash of the entry immediately before it. In production, hashes are computed over a canonical JSON serialisation of the entry payload plus the previous entry's hash, then written to a write-once store.
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
